// src/components/notify/notifyFormat.js
// FlowSense Notify — shared display helpers (categories, priorities, time, grouping)
import {
    MdAssignment, MdBuild, MdTimer, MdWarningAmber, MdFactCheck, MdEvent, MdReport, MdAutoAwesome,
    MdTrendingUp, MdCampaign, MdSettings, MdNotifications,
} from 'react-icons/md';

export const CATEGORY_META = Object.freeze({
    assignments:   { label: 'Work assignments',   icon: MdAssignment,  color: 'purple' },
    work_orders:   { label: 'Work order updates', icon: MdBuild,       color: 'blue' },
    sla:           { label: 'SLA & deadlines',    icon: MdTimer,       color: 'orange' },
    alerts:        { label: 'Critical alerts',    icon: MdWarningAmber, color: 'red' },
    inspections:   { label: 'Inspections & QA',   icon: MdFactCheck,   color: 'teal' },
    maintenance:   { label: 'Maintenance',        icon: MdEvent,       color: 'cyan' },
    issues:        { label: 'Reported issues',    icon: MdReport,      color: 'pink' },
    ai:            { label: 'FlowSense AI',       icon: MdAutoAwesome, color: 'purple' },
    escalation:    { label: 'Escalations',        icon: MdTrendingUp,  color: 'red' },
    announcements: { label: 'Announcements',      icon: MdCampaign,    color: 'green' },
    system:        { label: 'System',             icon: MdSettings,    color: 'gray' },
    general:       { label: 'General',            icon: MdNotifications, color: 'gray' },
});

export const categoryMeta = (c) => CATEGORY_META[c] || CATEGORY_META.general;

export const PRIORITY_META = Object.freeze({
    low:      { label: 'Low',      color: 'gray',   rank: 0 },
    normal:   { label: 'Normal',   color: 'blue',   rank: 1 },
    high:     { label: 'High',     color: 'orange', rank: 2 },
    critical: { label: 'Critical', color: 'red',    rank: 3 },
});
export const priorityMeta = (p) => PRIORITY_META[p] || PRIORITY_META.normal;

export const SOURCE_META = Object.freeze({
    console:            { label: 'Console',          ai: false },
    system:             { label: 'System',           ai: false },
    orchestrator:       { label: 'Automation',       ai: false },
    sla_ai:             { label: 'SLA AI',           ai: true },
    watchdog:           { label: 'AI Watchdog',      ai: true },
    dispatch_ai:        { label: 'Dispatch AI',      ai: true },
    notify_escalation:  { label: 'Auto-escalation',  ai: true },
    legacy_fcm:         { label: 'Legacy',           ai: false },
    legacy_model:       { label: 'Legacy',           ai: false },
    scheduled_legacy:   { label: 'Scheduled',        ai: false },
    test:               { label: 'Test',             ai: false },
    queue:              { label: 'Queue',            ai: false },
});
export const sourceMeta = (s) => SOURCE_META[s] || { label: s || 'System', ai: false };

export const STATUS_META = Object.freeze({
    queued:     { label: 'Queued',     color: 'gray' },
    scheduled:  { label: 'Scheduled',  color: 'blue' },
    processing: { label: 'Sending',    color: 'purple' },
    sent:       { label: 'Sent',       color: 'green' },
    partial:    { label: 'Partial',    color: 'orange' },
    failed:     { label: 'Failed',     color: 'red' },
    cancelled:  { label: 'Cancelled',  color: 'gray' },
    deduped:    { label: 'Duplicate',  color: 'gray' },
});
export const statusMeta = (s) => STATUS_META[s] || { label: s || '—', color: 'gray' };

const toDate = (v) => (v instanceof Date ? v : v ? new Date(v) : null);

export function timeAgo(v, now = Date.now()) {
    const d = toDate(v);
    if (!d || Number.isNaN(d.getTime())) return '';
    const s = Math.round((now - d.getTime()) / 1000);
    if (s < -60) {
        const m = Math.round(-s / 60);
        if (m < 60) return `in ${m} min`;
        const h = Math.round(m / 60);
        return h < 24 ? `in ${h} h` : `in ${Math.round(h / 24)} d`;
    }
    if (s < 45) return 'just now';
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} h ago`;
    const days = Math.round(h / 24);
    if (days < 7) return `${days} d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function fmtDateTime(v) {
    const d = toDate(v);
    if (!d || Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function fmtDuration(sec) {
    if (sec === null || sec === undefined || Number.isNaN(Number(sec))) return '—';
    const s = Math.round(Number(sec));
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    return `${h} h ${m % 60 ? `${m % 60} min` : ''}`.trim();
}

export const pct = (v) => (v === null || v === undefined ? '—' : `${Math.round(Number(v) * 10) / 10}%`);

/** 'Today' | 'Yesterday' | 'Mon, Oct 12' */
export function dayLabel(v, now = new Date()) {
    const d = toDate(v);
    if (!d) return '';
    const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const diff = Math.round((startOf(now) - startOf(d)) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Groups notifications by day label, preserving order. */
export function groupByDay(items) {
    const out = [];
    let cur = null;
    items.forEach((n) => {
        const label = dayLabel(n.created_at);
        if (!cur || cur.label !== label) { cur = { label, items: [] }; out.push(cur); }
        cur.items.push(n);
    });
    return out;
}
